import React, { useMemo, useState, useRef, useCallback } from 'react'
import clsx from 'clsx'
import { makeStyles, useTheme } from '@mui/styles'
import Decimal from 'decimal.js'
import MultiUserIcon from '@assets/images/icons/add-ons/multi-user.svg'
import TimeTrackingIcon from '@assets/images/icons/add-ons/timetracking.svg'
import SignaturesIcon from '@assets/images/icons/add-ons/e-signing.svg'
import RecurringInvoiceIcon from '@assets/images/icons/add-ons/recurring-invoice.svg'
import LetterPaperIcon from '@assets/images/icons/add-ons/letter-paper.svg'
import StorageIcon from '@assets/images/icons/add-ons/storage.svg'
import PublicApiIcon from '@assets/images/icons/addons/addon-api-anbindung.svg?url'
import ShoppingCartIcon from '@assets/images/icons/shopping-cart.svg'
import ThinCheckIcon from '@assets/images/icons/check_thin.svg'
import ArrowDownIcon from '@assets/images/icons/v2/arrow-down.svg'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import CheckIcon from '@assets/images/icons/check.svg'
import WarningIcon from '@assets/images/icons/v2/warning.svg'
import lang from '@/lang'
import { ButtonSquare } from '@/modules/base/components/buttons'
import { Module } from '@/modules/moduleSubscription/types/module'
import ThemeInterface from '@/theme-interface'
import { ModuleTypes, dateConstants } from '@/modules/base/util/constants'
import ButtonIconSquare from '@/modules/base/components/buttons/icon-square/button-icon-square'
import QuantityInput from '@/modules/base/components/inputs/quantity-input/quantity-input'
import { Accordion, AccordionDetails, AccordionSummary, Tooltip, useMediaQuery } from '@mui/material'
import CustomScrollbar from '@/modules/base/components/custom-scrollbar/custom-scrollbar'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { getMaxCartCount, STORAGE_PACKAGE_SIZE_GB } from '@/modules/moduleSubscription/constants'

const useStyles = makeStyles<ThemeInterface>((theme) => ({
  titleWrapper: {
    fontSize: theme.fontSizes.smedium,
    fontWeight: theme.fontWeights.medium,
    color: theme.black,
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  container: {
    paddingTop: 30,
    height: 'auto',
    background: theme.white,
    border: theme.border,
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 404,
    width: 404,
    [theme.devices.mobile]: {
      paddingBottom: 5,
      width: '100%',
      maxWidth: 'unset',
    },
  },
  content: {
    padding: '0px 30px',
    gap: 30,
    [theme.devices.mobile]: {
      padding: '0px 20px',
    },
  },
  numberInputWrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  selectAmountWrapper: {
    display: 'flex',
    width: '100%',
    gap: 20,
    justifyContent: 'space-between',
    [theme.devices.mobile]: {
      justifyContent: 'unset',
    },
  },
  badgeWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 16,
    width: 16,
    background: theme.green,
    borderRadius: '50%',
  },
  price: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    paddingBottom: 10,
    marginTop: 30,
  },
  priceValue: {
    fontSize: theme.fontSizes.huge,
    fontWeight: theme.fontWeights.medium,
  },
  priceHint: {
    fontSize: theme.fontSizes.default,
    color: theme.fontColors.secondary,
  },
  details: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    padding: '0px 20px 10px 20px',
    overflow: 'hidden',
    [theme.devices.mobile]: {
      padding: '0px 20px 0px 20px',
    },
  },
  bulletPoints: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    [theme.devices.mobile]: {
      paddingBottom: 20,
    },
  },
  bulletPoint: {
    display: 'flex',
    gap: 10,
    alignItems: 'center',
  },
  disabledHint: {
    marginBottom: 14,
    display: 'flex',
    alignItems: 'center',
    fontWeight: theme.fontWeights.medium,
    gap: 10,
  },
  divider: {
    width: '100%',
    height: 1,
    background: theme.borderColor,
    margin: '20px 0px',
    [theme.devices.mobile]: {
      margin: '20px 0px 10px 0px',
    },
  },
  accordion: {
    border: 'none',
    boxShadow: 'none',
    '& .MuiAccordion-heading': {
      '& .Mui-expanded': {
        minHeight: 'unset',
      },
      '& > button': {
        padding: 0,
        minHeight: 'unset',
        height: 45,
      },
    },
  },
  accordionContent: {
    padding: 0,
  },
  scrollbar: {
    marginBottom: 20,
  },
  disabled: {
    background: theme.backgroundColor,
    cursor: 'not-allowed',
    '& $accordion': {
      backgroundColor: theme.backgroundColor,
    },
  },
  tooltip: {
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSizes.tooltip,
    fontWeight: theme.fontWeights.regular,
  },
}))

interface ModuleItemProps {
  module: Module;
  type: string;
  onAddModuleToCart: (type: string, module: Module, value: number) => void;
  disabled?: boolean;
  disabledBy?: string;
  customClass?: string;
  inCart?: boolean;
  selectedPeriod?: string
}

export const ModuleIconMap: Record<string, React.ReactNode> = {
  [ModuleTypes.MULTI_USER]: MultiUserIcon,
  [ModuleTypes.TIME_TRACKING]: TimeTrackingIcon,
  [ModuleTypes.SIGNATURES]: SignaturesIcon,
  [ModuleTypes.RECURRING_INVOICES]: RecurringInvoiceIcon,
  [ModuleTypes.MULTI_LETTERPAPER]: LetterPaperIcon,
  [ModuleTypes.STORAGE_PACKAGE]: StorageIcon,
  [ModuleTypes.PUBLIC_API]: PublicApiIcon,
}

const formatStorageAmount = (count: number) => lang.moduleSubscription.list.moduleItem.storageAmount(count * STORAGE_PACKAGE_SIZE_GB)

const ModuleItem: React.FC<ModuleItemProps> = (props) => {
  const {
    module = {} as Module,
    type,
    onAddModuleToCart = () => {},
    disabled = false,
    disabledBy,
    inCart = false,
    customClass,
    selectedPeriod = dateConstants.YEAR,
  } = props
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.devices.mobile)
  const accordionDetailsRef = useRef<HTMLDivElement>(null)

  const [count, setCount] = useState(1)

  const itemIcon = ModuleIconMap[module.name]
    ? (
      <SVGIcon svg={ModuleIconMap[module.name]} height='30px' width='30px' />
    )
    : null

  const ownedInOtherSubs = module.singleton ? module.otherSubscriptions.reduce((result, m) => result + m.owned, 0) : 0
  const max = module.bookable === 0 ? undefined : module.bookable < 0 ? 0 : module.bookable - ownedInOtherSubs
  const ownedMax = Boolean(max <= 0 && (module.owned || ownedInOtherSubs))
  const isStoragePackage = module.name === ModuleTypes.STORAGE_PACKAGE
  const maxCartCount = getMaxCartCount(module.name)
  const showStorageAmount = isStoragePackage && (ownedMax || max >= 1)

  const price = useMemo(() => {
    const price = module.prices.find(price => price.period === selectedPeriod)?.values[0]
    return new Decimal(selectedPeriod === dateConstants.YEAR
      ? new Decimal(price.pricePerDuration).dividedBy(12).toDP(2).toNumber()
      : price.pricePerDuration).toDP(2).toNumber()
  }, [module, selectedPeriod])

  const action = useMemo(() => {
    if (max <= 1 && !showStorageAmount) {
      const buttonLabel = ownedMax ? lang.upgradeModal.modules.buttons.owned : inCart ? lang.upgradeModal.modules.buttons.inCart : lang.upgradeModal.modules.buttons.addToCart
      return (
        <ButtonSquare
          disabled={ownedMax || disabled}
          className={classes.button}
          onClick={() => {
            setCount(1)
            onAddModuleToCart(type, module, 1)
            toast.showToast({ type: 'success', message: lang.upgradeModal.modules.toast.addedtoCart })
          }
          }>
          {buttonLabel}
        </ButtonSquare>
      )
    }

    return (
      <div className={classes.selectAmountWrapper}>
        <QuantityInput
          key={ownedMax ? 'owned' : 'bookable'}
          min={1}
          max={ownedMax ? module.owned : Math.min(max ?? maxCartCount, maxCartCount)}
          defaultValue={ownedMax ? module.owned : 1}
          disabled={ownedMax || Boolean(disabledBy) || disabled}
          formatValue={isStoragePackage ? formatStorageAmount : undefined}
          onChange={(value) => setCount(value)}
        />
        <ButtonIconSquare
          icon={ShoppingCartIcon}
          disabled={count === 0 || ownedMax || disabled}
          className={classes.button}
          debounceTime={0}
          onClick={() => {
            onAddModuleToCart(type, module, count)
            toast.showToast({ type: 'success', message: lang.upgradeModal.modules.toast.addedtoCart })
          }}
        />
      </div>
    )
  }, [max, ownedMax, showStorageAmount, disabledBy, disabled, count, inCart, onAddModuleToCart])

  const ownedBadge = useMemo(() => {
    if (ownedMax || disabledBy) {
      return (
        <div className={classes.badgeWrapper}>
          <SVGIcon svg={CheckIcon} height='8px' width='8px' fill={theme.white} />
        </div>
      )
    }
  }, [ownedMax, disabledBy])

  const bulletPoints = useMemo(() => {
    const descriptions = lang.moduleSubscription.list.moduleItem.descriptions[module.name]
    const bulletpoints: string[] = typeof descriptions === 'function'
      ? descriptions({ price, packageSize: STORAGE_PACKAGE_SIZE_GB })
      : descriptions ?? []

    return (
      <div className={classes.bulletPoints}>
        {bulletpoints.map((bulletpoint) => (
          <div key={`${module.name}-${bulletpoint}`} className={classes.bulletPoint}>
            <SVGIcon svg={ThinCheckIcon} height='16px' width='16px' fill={theme.primary} />
            {bulletpoint}
          </div>
        ))}
      </div>
    )
  }, [module, price])

  const disabledHint = useMemo(() => {
    if (ownedMax) {
      return lang.upgradeModal.modules.disabledHint.owned
    }

    if (inCart) {
      return lang.upgradeModal.modules.disabledHint.inCart
    }

    return lang.upgradeModal.modules.disabledHint.default
  }, [ownedMax, inCart])

  const handleAccordionChange = useCallback((event: React.SyntheticEvent, isExpanded: boolean) => {
    if (isExpanded && accordionDetailsRef.current) {
      setTimeout(() => {
        accordionDetailsRef.current?.scrollIntoView({ behavior: 'smooth' })
      })
    }
  }, [])

  const details = isMobile
    ? (
      <Accordion classes={{ root: classes.accordion }} square onChange={handleAccordionChange}>
        <AccordionSummary expandIcon={<SVGIcon svg={ArrowDownIcon} height={16} width={16} fill={theme.primaryV2} />}>
          {lang.upgrade.wizard.subscriptionItem.expand}
        </AccordionSummary>
        <AccordionDetails ref={accordionDetailsRef} classes={{ root: classes.accordionContent }}>
          {(ownedMax || disabledBy || disabled) && (
            <div className={classes.disabledHint}>
              <SVGIcon svg={WarningIcon} height={16} width={16} fill={theme.orange} />
              {disabledHint}
            </div>
          )}
          {bulletPoints}
        </AccordionDetails>
      </Accordion>
    )
    : (
      <CustomScrollbar className={classes.scrollbar} maxHeight={150} autoHide={false}>
        {bulletPoints}
      </CustomScrollbar>
    )

  const itemContent = (
    <>
      <div className={classes.content}>
        <div>
          <div className={classes.titleWrapper}>
            {itemIcon}
            {module.name === ModuleTypes.MULTI_USER ? `Weitere ${module.title}` : module.title}
            {ownedBadge}
          </div>
          <div className={clsx(classes.price, classes.padding)}>
            <div className={classes.priceValue}>{lang.moduleSubscription.list.moduleItem.price(price)}</div>
            <div className={classes.priceHint}>{lang.moduleSubscription.list.moduleItem.priceHint}</div>
          </div>
        </div>
        <div className={clsx(classes.numberInputWrapper, classes.padding)}>
          {action}
        </div>
      </div>
      <div className={classes.divider} />
      <div className={classes.details} ref={accordionDetailsRef}>
        {details}
      </div>
    </>
  )

  const content = disabled || ownedMax
    ? (
      <Tooltip
        title={disabledHint}
        arrow
        placement="top"
        classes={{ tooltip: classes.tooltip }}
      >
        <div className={classes.disabledCursor}>
          {itemContent}
        </div>
      </Tooltip>
    )
    : itemContent

  return (
    <div className={clsx(classes.container, (disabled || ownedMax) && classes.disabled, customClass)}>
      {content}
    </div>
  )
}

export default ModuleItem
